import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFailQaWithDefectsMutation } from '@/redux/internal-services/jobsApi';
import { Button } from '@/components/UI/Button';

/** JOB-FEAT-05 — Fail QA capture, itemized. Mockup's QA-failed rework view shows individual rework
 *  tasks per defect (e.g. "Ridge cap not sealed..." and "Tear-off debris..." as two separate
 *  Crew tasks), not one generic reason string — this replaces the generic single-reason textarea
 *  for `fail_qa_rework` specifically (see WorkflowCard.tsx's clickTransition special-case) with a
 *  real add/remove defect-line list, each becoming its own task via
 *  POST /engagements/{id}/qa/fail (api-service JobService.fail_qa_with_defects). */
const QaFailForm: React.FC<{
  engagementId: string;
  onFailed?: () => void;
  onCancel?: () => void;
}> = ({ engagementId, onFailed, onCancel }) => {
  const { t } = useTranslation();
  const [failQa, { isLoading }] = useFailQaWithDefectsMutation();
  const [reason, setReason] = useState('');
  const [items, setItems] = useState<string[]>(['']);
  const [error, setError] = useState('');

  const updateItem = (i: number, value: string) => {
    setItems((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  };
  const addItem = () => setItems((prev) => [...prev, '']);
  const removeItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t('jobs.qaFailReasonRequired', 'Enter a reason.'));
      return;
    }
    setError('');
    try {
      await failQa({
        engagementId,
        reason: reason.trim(),
        defect_items: items.map((d) => d.trim()).filter(Boolean).map((description) => ({ description })),
      }).unwrap();
      onFailed?.();
    } catch (err) {
      setError((err as { data?: { message?: string } })?.data?.message || t('jobs.qaFailFailed', 'Could not fail QA.'));
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-2">
      {error && <p className="rounded-md bg-red-500/10 px-2 py-1.5 text-[11px] text-[#CF4148]">{error}</p>}
      <div>
        <label className="mb-0.5 block text-[10px] text-ui-text3">{t('jobs.qaFailReason', 'Reason (required)')}</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] text-ui-text3">{t('jobs.reworkItems', 'Rework items (optional — each becomes its own Crew task)')}</label>
          <button type="button" onClick={addItem} className="text-[11px] font-medium text-brand-dark hover:underline">
            + {t('jobs.addItem', 'Add item')}
          </button>
        </div>
        <div className="space-y-1.5">
          {items.map((value, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                value={value}
                onChange={(e) => updateItem(i, e.target.value)}
                placeholder={t('jobs.defectDescriptionPlaceholder', 'e.g. "Ridge cap not sealed on the west run"')}
                className="flex-1 rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none"
              />
              {items.length > 1 && (
                <button type="button" onClick={() => removeItem(i)} className="text-ui-text3 hover:text-[#CF4148]" aria-label={t('common.remove', 'Remove')}>✕</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" disabled={isLoading} onClick={onCancel}>{t('workflow.cancel', 'Cancel')}</Button>
        )}
        <Button type="submit" size="sm" disabled={isLoading || !reason.trim()}>
          {isLoading ? t('jobs.failingQa', 'Failing…') : t('jobs.confirmFailQa', 'Confirm Fail QA')}
        </Button>
      </div>
    </form>
  );
};

export default QaFailForm;
